import { createAction } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { analyticsService, EventSource } from '../../utils/analyticsService';
import {
    ymTrackCollectionReport,
    ymTrackOpenCollectionLike,
    ymTrackOpenCollectionShared,
} from '../../utils/metrika';
import { SharingMethod } from '../../utils/share';
import { showNotificationAsync, showToastAsync } from '../notifications/actions';
import { removeSavedCollectionFromPost, saveCollectionFromPost } from '../posts/actions';
import { ReportReasons } from '../posts/types';
import { RootState } from '../store';
import {
    selectCollectionById,
    selectCollectionsLoading,
    selectCollectionsNext,
    selectCollectionsNextSaved,
} from './selectors';
import * as collectionsService from './service';
import type { CollectionModel, CollectionsDataModel } from './types';

export const setCollectionsRecords = createAction(
    '@@collections/SET_COLLECTIONS_RECORDS',
    (...payload: CollectionsDataModel[]) => ({ payload }),
);

export const setCollectionIdentifiers = createAction<{
    namespace: string;
    identifiers: Array<CollectionsDataModel['id']>;
}>('@@collections/SET_COLLECTIONS_IDENTIFIERS');

export const addNewCollectionIdentifiers = createAction<{
    namespace: string;
    identifiers: Array<CollectionsDataModel['id']>;
}>('@@collections/ADD_NEW_COLLECTIONS_IDENTIFIERS');

export const appendCollectionIdentifiers = createAction<{
    namespace: string;
    identifiers: Array<CollectionsDataModel['id']>;
}>('@@collections/APPEND_COLLECTION_IDENTIFIERS');

export const setCollectionsNext = createAction<{
    namespace: string;
    next?: string;
    nextSaved?: string;
}>('@@collections/SET_COLLECTIONS_NEXT');

export const setCollectionsLoading = createAction<{
    namespace: string;
    isLoading: boolean;
}>('@@collections/SET_COLLECTIONS_LOADING');

export const setCollectionsError = createAction<{
    namespace: string;
    hasError: boolean;
}>('@@collections/SET_COLLECTIONS_ERROR');

export const setCollectionsLoaded = createAction<{
    namespace: string;
    created?: boolean;
    saved?: boolean;
}>('@@collections/SET_COLLECTIONS_LOADED');

export const removeCollectionRecord = createAction<CollectionsDataModel['id']>(
    '@@collections/REMOVE_COLLECTION',
);

export const removeSavedCollectionRecord = createAction<CollectionsDataModel['id']>(
    '@@collections/REMOVE_SAVED_COLLECTION',
);

export const saveCollection = createAction<CollectionsDataModel['id']>(
    '@@collections/SAVE_COLLECTION',
);

export function loadCollectionsAsync(
    businessId?: string,
    options?: { skipSavedCollections?: boolean },
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const namespace = businessId || 'default';

        if (selectCollectionsLoading(getState(), namespace)) {
            return;
        }

        dispatch(setCollectionsLoading({ namespace, isLoading: true }));
        dispatch(setCollectionsError({ namespace, hasError: false }));

        try {
            const response = await collectionsService.loadCollections({ businessId });
            const savedCollectionsResponse =
                businessId || options?.skipSavedCollections
                    ? null
                    : await collectionsService.loadCollections({
                          type: collectionsService.CollectionType.SAVED,
                      });

            dispatch(
                setCollectionsRecords(
                    ...response.records.collections,
                    ...(savedCollectionsResponse
                        ? savedCollectionsResponse.records.collections
                        : []),
                ),
            );
            dispatch(
                setCollectionIdentifiers({
                    namespace,
                    identifiers: [
                        ...response.collections,
                        ...(savedCollectionsResponse ? savedCollectionsResponse.collections : []),
                    ],
                }),
            );
            dispatch(
                setCollectionsNext({
                    namespace,
                    next: response.next,
                    nextSaved: savedCollectionsResponse?.next,
                }),
            );
            dispatch(
                setCollectionsLoaded({
                    namespace,
                    created: true,
                    saved: savedCollectionsResponse !== null,
                }),
            );
        } catch (_error) {
            dispatch(setCollectionsError({ namespace, hasError: true }));
        }

        dispatch(setCollectionsLoading({ namespace, isLoading: false }));
    };
}

export function loadMoreCollectionsAsync(options?: {
    businessId?: string;
    type?: collectionsService.CollectionType;
}): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const { businessId, type } = options ?? {};
        const namespace = businessId || 'default';
        const isSaved = type === collectionsService.CollectionType.SAVED;
        const cursor = isSaved
            ? selectCollectionsNextSaved(getState(), namespace)
            : selectCollectionsNext(getState(), namespace);

        if (!cursor) {
            return;
        }

        dispatch(setCollectionsLoading({ namespace, isLoading: true }));
        dispatch(setCollectionsError({ namespace, hasError: false }));

        try {
            const response = await collectionsService.loadCollections({
                businessId: isSaved ? undefined : businessId,
                type,
                next: cursor,
            });

            dispatch(setCollectionsRecords(...response.records.collections));
            dispatch(appendCollectionIdentifiers({ namespace, identifiers: response.collections }));
            dispatch(
                setCollectionsNext({
                    namespace,
                    ...(isSaved ? { nextSaved: response.next } : { next: response.next }),
                }),
            );
        } catch (_error) {
            dispatch(setCollectionsError({ namespace, hasError: true }));
        }

        dispatch(setCollectionsLoading({ namespace, isLoading: false }));
    };
}

export function loadCollectionAsync(
    collectionId: CollectionsDataModel['id'],
    namespace: string = 'default',
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        dispatch(setCollectionsLoading({ namespace, isLoading: true }));

        try {
            const collection = await collectionsService.loadCollection(collectionId);

            dispatch(setCollectionsRecords(collection));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить коллекцию'));
            throw error;
        } finally {
            dispatch(setCollectionsLoading({ namespace, isLoading: false }));
        }
    };
}

export function removeCollectionAsync(
    collectionId: CollectionsDataModel['id'],
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await collectionsService.removeCollection(collectionId);
            dispatch(removeCollectionRecord(collectionId));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось удалить коллекцию'));
        }
    };
}

export function saveCollectionAsync(
    collectionId: CollectionsDataModel['id'],
    moveToSavedCollections?: () => void,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await collectionsService.saveCollection(collectionId);

            await dispatch(saveCollection(collectionId));
            await dispatch(saveCollectionFromPost({ collectionId }));

            await dispatch(
                showToastAsync({
                    message: 'Коллекция сохранена в вашем профиле',
                    actionText: 'Перейти',
                    onClick: () => {
                        moveToSavedCollections?.();
                    },
                }),
            );
        } catch (_error) {
            await dispatch(showNotificationAsync('Не удалось сохранить коллекцию'));
        }
    };
}

export function removeSavedCollectionAsync(
    collectionId: CollectionsDataModel['id'],
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await collectionsService.removeSavedCollection(collectionId);
            dispatch(removeSavedCollectionRecord(collectionId));
            dispatch(removeSavedCollectionFromPost({ collectionId }));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось удалить коллекцию'));
        }
    };
}

export function shareCollectionAsync(
    collectionId: CollectionModel['id'],
    sharingUrl: CollectionModel['sharingUrl'],
    login?: string,
    businessId?: string,
    isPublic?: CollectionModel['isPublic'],
    modifiable?: CollectionModel['modifiable'],
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            const sharingMethod = await collectionsService.shareCollection(sharingUrl);

            if (sharingMethod === SharingMethod.CLIPBOARD) {
                dispatch(showNotificationAsync('Скопировано в буфер обмена'));
            }

            analyticsService.trackCollectionShare({
                collectionId: collectionId,
                eventSource: EventSource.COLLECTION,
            });

            ymTrackOpenCollectionShared({
                page: 'Коллекция',
                userLogin: login,
                businessId: businessId,
                isPublic,
                own: modifiable,
                collectionId,
            });
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось поделиться данными'));
        }
    };
}

export function reportCollectionAsync(
    targetId: string,
    reason: ReportReasons,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await collectionsService.reportCollection(targetId, reason);

            dispatch(addReportedCollection(targetId));

            ymTrackCollectionReport({
                page: 'Коллекция',
                collectionId: targetId,
                reason: reason,
            });

            dispatch(showNotificationAsync('Мы будем показывать меньше таких публикаций.'));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось отправить жалобу на коллекцию.'));
        }
    };
}

export function updateCollectionReactionAsync({
    collectionId,
    login,
    businessId,
    isPublic,
    own,
    eventSource,
}: {
    collectionId: string;
    login?: string;
    businessId?: string;
    isPublic?: boolean;
    own?: boolean;
    eventSource?: EventSource;
}): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const model = selectCollectionById(getState(), collectionId);

        if (!model) {
            return;
        }

        dispatch(setCollectionReaction({ id: collectionId, isLiked: !model.isLiked }));

        try {
            await collectionsService.updateCollectionReaction(
                model,
                eventSource ?? EventSource.COLLECTION,
            );
            ymTrackOpenCollectionLike({
                page: 'Коллекция',
                userLogin: login,
                businessId: businessId,
                isPublic: isPublic,
                own: own,
                collectionId: collectionId,
                postReaction: model.isLiked ? 'like' : 'unlike',
            });
        } catch (_error) {
            dispatch(setCollectionReaction({ id: collectionId, isLiked: model.isLiked }));
        }
    };
}

export const setCollectionReaction = createAction<{
    id: CollectionsDataModel['id'];
    isLiked: CollectionsDataModel['isLiked'];
}>('@@collections/SET_REACTION');

export const addReportedCollection = createAction<string>('@@collections/ADD_REPORTED_COLLECTION');
